import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  Communication,
  CommunicationAttachment,
  communicationService,
} from '../../services/communicationService';
import { getApiError } from '../../utils/apiError';
import { formatFileSize } from '../../utils/formatFileSize';

interface Props {
  comm: Communication;
}

/**
 * The files on a message, each one downloadable.
 *
 * Styled as the Files tab styles a document, because that is what these are to
 * the person reading them - the only difference is that they belong to the
 * message rather than to the patient's record.
 */
const CommunicationAttachmentList: React.FC<Props> = ({ comm }) => {
  const attachments = comm.attachments || [];
  const [downloading, setDownloading] = useState<number | null>(null);

  if (attachments.length === 0) return null;

  const handleDownload = async (attachment: CommunicationAttachment) => {
    try {
      setDownloading(attachment.id);
      await communicationService.downloadAttachment(
        comm.id, attachment.id, attachment.file_name,
      );
    } catch (err) {
      toast.error(getApiError(err, 'Failed to download attachment'));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold text-gray-500">
        {attachments.length} attachment{attachments.length === 1 ? '' : 's'}
      </div>
      {attachments.map((attachment) => (
        <div
          key={attachment.id}
          className="bg-[#d4e8e5] rounded-xl p-3 flex items-center justify-between gap-3"
        >
          <div className="min-w-0">
            <div className="text-sm font-medium text-[#366A75] truncate">
              {attachment.file_name}
            </div>
            <div className="text-xs text-gray-600">
              {formatFileSize(attachment.file_size)}
            </div>
          </div>
          <button
            type="button"
            onClick={() => handleDownload(attachment)}
            disabled={downloading === attachment.id}
            title="Download"
            aria-label={`Download ${attachment.file_name}`}
            className="p-1.5 hover:bg-white/50 rounded transition-colors disabled:opacity-50 shrink-0"
          >
            <svg className="w-5 h-5 text-[#366A75]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3"
              />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
};

export default CommunicationAttachmentList;
